import { createContext, useContext, useEffect } from 'react';

/**
 * PresenceContext: who is online (Support V1 phase 3; agent_docs/support_v1/REQUIREMENTS.md section 7; Arjun's design,
 * CEL-3859 section 1). Mounted once for the signed-in app.
 *
 * - The ping: POST /presence/ping ONLY while document.visibilityState === 'visible' AND there was input (a key, a pointer
 *   press, the wheel, a touch; never a scroll) in the last ACTIVE_WINDOW_MS, at most once per PING_EVERY_MS per tab (shouldPing). Never the user
 *   WebSocket's 30 s ping (it fires from hidden tabs). Never while an admin is spoofing.
 * - The read: one GET /presence for every user id a mounted surface watches (useWatchPresence) plus the viewer, every
 *   READ_EVERY_MS while the tab is visible, and soon after a new id is watched. People only: an AI employee never carries a
 *   dot (components/Common/PresenceDot renders nothing for them).
 * - away: the viewer's own "Set yourself as away" switch (stored on the server, stays until changed).
 * The provider (the ping, the read, the switch) is PresenceProvider.js, mounted once in App.js; this module holds the
 * context, the hooks and the rules, so a surface that shows a dot never loads the API client.
 */
export const PING_EVERY_MS = 50 * 1000;
export const ACTIVE_WINDOW_MS = 2 * 60 * 1000;
export const READ_EVERY_MS = 60 * 1000;

// The one rule for the heartbeat (exported for its test): visible, active, and not pinged in the last PING_EVERY_MS.
export function shouldPing({ visible, lastInputAt, lastPingAt, now }) {
  return !!visible && now - lastInputAt <= ACTIVE_WINDOW_MS && now - lastPingAt >= PING_EVERY_MS;
}

const EMPTY = new Set();
const FALLBACK = { me: '', online: EMPTY, away: false, isOnline: () => false, setAway: async () => {}, watch: () => () => {} };
export const PresenceContext = createContext(FALLBACK);

export const usePresence = () => useContext(PresenceContext) || FALLBACK;

// Watch these user ids while the calling surface is mounted (the read asks about them).
export function useWatchPresence(ids) {
  const { watch } = usePresence();
  const key = Array.from(new Set((ids || []).filter(Boolean).map(String))).sort().join(',');
  useEffect(() => watch(key ? key.split(',') : []), [key, watch]);
}

// "human:<id>" refs of a DM, minus the viewer: the one other person of a 1:1 human DM, else null (no dot for a group, an AI
// employee or a support visitor).
export function otherHumanOf(participants, viewerId) {
  const others = (participants || []).filter((r) => r !== `human:${viewerId}`);
  return others.length === 1 && String(others[0]).startsWith('human:') ? String(others[0]).slice(6) : null;
}
